import React, { forwardRef, useMemo } from "react";
import Select from "./Select";

// Fontaine M38 — SortSelect (`ft-sort`). The one place the app turns "a sort choice" into the
// `{ field, direction }` pair every list endpoint already speaks.
//
// It is Select — same popover on laptop, same bottom sheet on phone, same keyboard model — with
// one job added: it owns the field/direction encoding so no page has to. Before this, each page
// kept a `"created_at|desc"` string in state and split it on `|` at the call site; the string
// leaked into the request builder and the delimiter was re-invented per page.
//
// It is CONTROLLED, like DataTable's sort: it stores nothing, it renders the pair it is handed
// and reports `onChange(field, direction)` — deliberately the same signature as DataTable's
// `onSortChange`, so a page can wire a sort control and a sortable table header to one handler.
//
// No stylesheet of its own: a sort control is a Select, and giving it a second look would be
// exactly the drift the design system exists to prevent.

/**
 * @typedef {{ field: string, direction: "asc"|"desc", label: string }} SortOption
 *
 * @param {SortOption[]} options
 * @param {string} [field]                         currently sorted field
 * @param {"asc"|"desc"} [direction]               current direction
 * @param {(field: string, direction: string) => void} onChange
 * @param {string} [label="Sort"]                  accessible name for the control
 * @param {"md"|"lg"} [size="md"]
 */
const SortSelect = forwardRef(function SortSelect(
  { options = [], field, direction, onChange, label = "Sort", className = "", ...rest },
  ref
) {
  // `field|direction` is an internal detail of this component. It exists only because Select's
  // option value must be a scalar; it never leaves here in either direction.
  const selectOptions = useMemo(
    () => options.map((o) => ({ value: `${o.field}|${o.direction}`, label: o.label })),
    [options]
  );

  const value = field ? `${field}|${direction}` : "";

  return (
    <Select
      ref={ref}
      className={["ft-sort", className].filter(Boolean).join(" ")}
      options={selectOptions}
      value={value}
      label={label}
      placeholder={label}
      onChange={(next) => {
        const [nextField, nextDirection] = String(next).split("|");
        onChange?.(nextField, nextDirection);
      }}
      {...rest}
    />
  );
});

export default SortSelect;
